import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Download, Layers, Loader2, Copy, Check, X,
  Search, Eye, MoreVertical, Trash2, Image, FileText, ChevronDown,
  Sparkles, Plus, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, BarChart3
} from 'lucide-react'
import {
  adUnitsApi, AdUnit, NETWORK_OPTIONS, NetworkKey,
  PublishTagsResponse, PublishedTag, PreviewUrls
} from '../../lib/api/adUnits'
import { ProcessingAdUnit, CreatedAdUnit } from '../../lib/actionCable'
import { CreateAdUnitModal } from '../creative-studio/CreateAdUnitModal'
import toast from 'react-hot-toast'

interface CreativeGenerationTabProps {
  campaignId?: number
  /**
   * Advertiser the current campaign belongs to. Forwarded to
   * {@link CreateAdUnitModal} so it can fetch the content-type + size
   * allowlist for that advertiser. When `null`/`undefined`, the
   * "Create new" button is disabled because the catalog endpoint is
   * advertiser-scoped and we don't want to fall back to stale hard-coded
   * options.
   */
  advertiserId?: number | null
  processingAdUnits?: ProcessingAdUnit[]
  completedAdUnits?: CreatedAdUnit[]
  isLoading?: boolean
  onOpenStudio?: () => void
  onOpenAdUnit?: (adUnitId: number) => void
  onViewDashboard?: (adUnitId: number) => void
}

interface DisplayAdUnit {
  id: number
  ad_unit_id?: number
  campaign_id: number
  name?: string
  content_type: string
  sizes: string[]
  status: 'processing' | 'completed' | 'active' | string
  preview_url?: string
  preview_urls?: PreviewUrls
  preview_static_urls?: Array<{ size: string; url: string }>
  ad_ids?: number[]
  creative_manifest?: any[]
  created_at?: string
  updated_at?: string
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMin / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffMonths = Math.floor(diffDays / 30)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 30) return `${diffDays}d ago`
  if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`
  return date.toLocaleDateString()
}

function getPreviewUrl(adUnit: DisplayAdUnit): string | null {
  return adUnit.preview_urls?.base || adUnit.preview_url || null
}


export function CreativeGenerationTab({
  campaignId,
  advertiserId = null,
  processingAdUnits = [],
  completedAdUnits = [],
  isLoading = false,
  onOpenStudio,
  onOpenAdUnit,
  onViewDashboard
}: CreativeGenerationTabProps) {
  const [apiAdUnits, setApiAdUnits] = useState<AdUnit[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pagination
  const [pagination, setPagination] = useState<{ current_page: number; total_pages: number; total_count: number }>({
    current_page: 1, total_pages: 1, total_count: 0
  })

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Context menu
  const [contextMenuId, setContextMenuId] = useState<number | null>(null)
  const contextMenuRef = useRef<HTMLDivElement>(null)

  // Delete confirmation
  const [deletingUnit, setDeletingUnit] = useState<DisplayAdUnit | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Copy in-progress
  const [copyingId, setCopyingId] = useState<number | null>(null)

  // Create ad unit modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // Network / Tags modals (preserved from original)
  const [networkModalAdUnit, setNetworkModalAdUnit] = useState<DisplayAdUnit | null>(null)
  const [networkSearchQuery, setNetworkSearchQuery] = useState('')
  const [tagsModalData, setTagsModalData] = useState<{
    adUnit: DisplayAdUnit
    response: PublishTagsResponse
  } | null>(null)
  const [publishingNetwork, setPublishingNetwork] = useState<NetworkKey | null>(null)
  const [copiedTagIndex, setCopiedTagIndex] = useState<number | null>(null)

  // ---------- Merge ad units ----------
  const displayAdUnits = useMemo<DisplayAdUnit[]>(() => {
    const units: DisplayAdUnit[] = []
    const seenIds = new Set<number>()

    processingAdUnits.forEach(unit => {
      units.push({
        id: unit.id,
        campaign_id: unit.campaign_id,
        content_type: unit.content_type,
        sizes: unit.sizes || [],
        status: 'processing'
      })
      seenIds.add(unit.id)
    })

    apiAdUnits.forEach(unit => {
      const unitId = unit.ad_unit_id || unit.id
      if (!seenIds.has(unitId)) {
        units.push({
          id: unit.id,
          ad_unit_id: unit.ad_unit_id,
          campaign_id: unit.campaign_id,
          name: unit.name,
          content_type: unit.content_type,
          sizes: unit.sizes || [],
          status: unit.status || 'active',
          preview_url: unit.preview_url,
          preview_urls: unit.preview_urls,
          creative_manifest: unit.creative_manifest,
          created_at: unit.created_at,
          updated_at: unit.updated_at
        })
        seenIds.add(unitId)
      }
    })

    completedAdUnits.forEach(unit => {
      if (!seenIds.has(unit.ad_unit_id)) {
        units.push({
          id: unit.ad_unit_id,
          ad_unit_id: unit.ad_unit_id,
          campaign_id: unit.campaign_id,
          content_type: unit.content_type,
          sizes: unit.sizes || [],
          status: 'completed',
          preview_url: unit.preview_url,
          preview_static_urls: unit.preview_static_urls,
          ad_ids: unit.ad_ids
        })
        seenIds.add(unit.ad_unit_id)
      }
    })

    return units
  }, [processingAdUnits, completedAdUnits, apiAdUnits])

  // ---------- Fetch ----------
  const fetchAdUnits = useCallback(async (page: number = 1) => {
    if (!campaignId) return
    setIsFetching(true)
    setError(null)
    try {
      const { adUnits: fetched, meta } = await adUnitsApi.getAdUnits(campaignId, { page })
      setApiAdUnits(fetched)
      if (meta) {
        setPagination(meta)
      }
    } catch {
      if (processingAdUnits.length === 0 && completedAdUnits.length === 0) {
        setError('Failed to load ad units. Please try again.')
      }
    } finally {
      setIsFetching(false)
    }
  }, [campaignId, processingAdUnits.length, completedAdUnits.length])

  useEffect(() => { fetchAdUnits(1) }, [campaignId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (completedAdUnits.length > 0 && campaignId) {
      const t = setTimeout(() => fetchAdUnits(pagination.current_page), 500)
      return () => clearTimeout(t)
    }
  }, [completedAdUnits.length, campaignId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.total_pages || isFetching) return
    fetchAdUnits(newPage)
  }

  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const { current_page, total_pages } = pagination
    const pages: (number | 'ellipsis')[] = []
    if (total_pages <= 7) {
      for (let i = 1; i <= total_pages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (current_page > 3) pages.push('ellipsis')
      const start = Math.max(2, current_page - 1)
      const end = Math.min(total_pages - 1, current_page + 1)
      for (let i = start; i <= end; i++) pages.push(i)
      if (current_page < total_pages - 2) pages.push('ellipsis')
      pages.push(total_pages)
    }
    return pages
  }

  // ---------- Close context menu on outside click ----------
  useEffect(() => {
    if (contextMenuId === null) return
    const handler = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenuId(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [contextMenuId])

  // ---------- Selection ----------
  const allSelected = displayAdUnits.length > 0 && selectedIds.size === displayAdUnits.length
  const someSelected = selectedIds.size > 0 && !allSelected

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(displayAdUnits.map(u => u.id)))
    }
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // ---------- Actions ----------
  const handleCopyAdUnit = async (adUnit: DisplayAdUnit) => {
    if (!campaignId) return
    const adUnitId = adUnit.ad_unit_id || adUnit.id
    setCopyingId(adUnitId)
    setContextMenuId(null)
    try {
      await adUnitsApi.copyAdUnit(campaignId, adUnitId)
      toast.success('Ad unit copied successfully')
      fetchAdUnits()
    } catch {
      toast.error('Failed to copy ad unit')
    } finally {
      setCopyingId(null)
    }
  }

  const handleDeleteAdUnit = async () => {
    if (!campaignId || !deletingUnit) return
    const adUnitId = deletingUnit.ad_unit_id || deletingUnit.id
    setIsDeleting(true)
    try {
      await adUnitsApi.deleteAdUnit(campaignId, adUnitId)
      toast.success('Ad unit deleted')
      setDeletingUnit(null)
      fetchAdUnits()
    } catch {
      toast.error('Failed to delete ad unit')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleOpenNetworkModal = (adUnit: DisplayAdUnit) => {
    setNetworkModalAdUnit(adUnit)
    setNetworkSearchQuery('')
    setContextMenuId(null)
  }

  const handlePublishTags = async (network: NetworkKey) => {
    if (!networkModalAdUnit || !campaignId || publishingNetwork) return
    const adUnitId = networkModalAdUnit.ad_unit_id || networkModalAdUnit.id
    setPublishingNetwork(network)
    try {
      const response = await adUnitsApi.publishTags(campaignId, adUnitId, network)
      if (!response?.js_tags || !Array.isArray(response.js_tags)) {
        toast.error('No tags available for this network. Please try a different network.')
        return
      }
      setTagsModalData({ adUnit: networkModalAdUnit, response })
      setNetworkModalAdUnit(null)
    } catch {
      toast.error('Failed to generate tags. Please try again.')
    } finally {
      setPublishingNetwork(null)
    }
  }

  const handleCopyTag = async (tag: PublishedTag, index: number) => {
    try {
      await navigator.clipboard.writeText(tag.content)
      setCopiedTagIndex(index)
      toast.success(`Tag for ${tag.size} copied to clipboard!`)
      setTimeout(() => setCopiedTagIndex(null), 2000)
    } catch {
      toast.error('Failed to copy tag')
    }
  }

  const filteredNetworks = NETWORK_OPTIONS.filter(n =>
    n.name.toLowerCase().includes(networkSearchQuery.toLowerCase()) ||
    n.key.toLowerCase().includes(networkSearchQuery.toLowerCase())
  )

  const hasUnits = displayAdUnits.length > 0
  const processingCount = processingAdUnits.length

  // ========== RENDER ==========

  if (!campaignId) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mx-auto mb-4">
            <Layers className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Creative Generation</h3>
          <p className="text-gray-500 dark:text-gray-400">
            Select templates from the Campaign Proposal tab and click "Create Ad Units" to generate creative assets.
          </p>
        </div>
      </div>
    )
  }

  if (isLoading && !hasUnits) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600" />
            <Layers className="h-6 w-6 text-primary-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Creating Ad Units</h3>
          <p className="text-gray-500 dark:text-gray-400">
            Generating ad units from the selected templates...
          </p>
        </div>
      </div>
    )
  }

  if (isFetching && !hasUnits) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Loading ad units...</p>
        </div>
      </div>
    )
  }

  if (error && !hasUnits) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error Loading Ad Units</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => fetchAdUnits(pagination.current_page)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // ---------- Empty State ----------
  if (!hasUnits) {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-gray-950">
        {/* Top bar with action buttons */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <button
            onClick={() => onOpenStudio?.()}
            className="inline-flex items-center gap-2 px-4 py-2 border border-primary-600 dark:border-primary-500 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-sm font-medium"
          >
            <Sparkles className="h-4 w-4" />
            Start with AI
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!campaignId || !advertiserId}
            title={
              !campaignId
                ? 'Campaign is required'
                : !advertiserId
                ? 'Advertiser not loaded yet'
                : undefined
            }
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            Create new
          </button>
        </div>
        {/* Empty body */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-5">
              <Image className="h-10 w-10 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Ad Units Yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
              Create ad units using AI or manually to start generating creative assets for this campaign.
            </p>
          </div>
        </div>

        {/* Create Ad Unit Modal also needs to mount in the empty state, otherwise
            clicking "Create new" only toggles state with no modal in the tree. */}
        {campaignId && (
          <CreateAdUnitModal
            isOpen={isCreateModalOpen}
            campaignId={campaignId}
            advertiserId={advertiserId}
            onClose={() => setIsCreateModalOpen(false)}
            onCreated={(id) => {
              setIsCreateModalOpen(false)
              onOpenAdUnit?.(id)
            }}
          />
        )}
      </div>
    )
  }

  // ---------- Main List View ----------
  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-950">
      {/* Toolbar */}
      <div className="flex items-center gap-3 pl-4 pr-4 lg:pr-8 py-3 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
        <div className="flex items-center justify-center w-8 flex-shrink-0">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => { if (el) el.indeterminate = someSelected }}
            onChange={toggleSelectAll}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent"
          />
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <ChevronDown className="h-4 w-4 text-gray-400" />
          {selectedIds.size > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {selectedIds.size}
            </span>
          )}
        </div>
        <div className="flex items-center flex-1 min-w-0">
          {processingCount > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-xs font-medium">
              <Loader2 className="h-3 w-3 animate-spin" />
              {processingCount} Processing
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onOpenStudio?.()}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-primary-600 dark:border-primary-500 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-sm font-medium"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Start with AI
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!campaignId || !advertiserId}
            title={
              !campaignId
                ? 'Campaign is required'
                : !advertiserId
                ? 'Advertiser not loaded yet'
                : undefined
            }
            className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-3.5 w-3.5" />
            Create new
          </button>
        </div>
      </div>

      {/* Column Header */}
      <div className="grid grid-cols-[32px_70px_1fr_140px_minmax(160px,1fr)_140px_100px] gap-3 pl-4 pr-4 lg:pr-8 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800/60 bg-gray-50/80 dark:bg-gray-900/50 flex-shrink-0">
        <div />
        <div className="pl-1">ID</div>
        <div>Name</div>
        <div>Type</div>
        <div>Sizes</div>
        <div>Last Modified</div>
        <div className="text-right">Options</div>
      </div>

      {/* Scrollable Rows — overlay scrollbar so it doesn't shift content width */}
      <div className="flex-1 relative scrollbar-thin" style={{ overflowY: 'overlay' as any }}>
        {isFetching ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading ad units...</p>
            </div>
          </div>
        ) : displayAdUnits.length === 0 ? null : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800/50">
        {displayAdUnits.map((adUnit) => {
          const isProcessing = adUnit.status === 'processing'
          const isSelected = selectedIds.has(adUnit.id)
          const isCopying = copyingId === (adUnit.ad_unit_id || adUnit.id)
          const isMenuOpen = contextMenuId === adUnit.id

          return (
            <div
              key={adUnit.id}
              className={`grid grid-cols-[32px_70px_1fr_140px_minmax(160px,1fr)_140px_100px] gap-3 pl-4 pr-4 lg:pr-8 py-3 items-center transition-colors group ${
                isProcessing
                  ? 'bg-amber-50/40 dark:bg-amber-900/5'
                  : isSelected
                  ? 'bg-primary-50/40 dark:bg-primary-900/10'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-900/40'
              }`}
            >
              {/* Checkbox */}
              <div className="flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelect(adUnit.id)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent"
                />
              </div>

              {/* ID */}
              <div className="min-w-0 pl-1">
                <button
                  onClick={() => !isProcessing && onOpenAdUnit?.(adUnit.ad_unit_id || adUnit.id)}
                  disabled={isProcessing}
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition-colors disabled:text-gray-400 disabled:no-underline disabled:cursor-default"
                >
                  {adUnit.ad_unit_id || adUnit.id}
                </button>
              </div>

              {/* Name */}
              <div className="min-w-0">
                <button
                  onClick={() => !isProcessing && onOpenAdUnit?.(adUnit.ad_unit_id || adUnit.id)}
                  disabled={isProcessing}
                  className="text-left max-w-full block w-full"
                  title={adUnit.name || adUnit.content_type || 'Untitled'}
                >
                  <p className={`text-sm font-medium truncate ${isProcessing ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors'}`}>
                    {adUnit.name || adUnit.content_type || 'Untitled'}
                  </p>
                </button>
              </div>

              {/* Type column (content_type / format family). */}
              <div className="min-w-0">
                <p
                  className="text-sm text-gray-900 dark:text-white truncate"
                  title={adUnit.content_type || 'Ad Unit'}
                >
                  {adUnit.content_type || 'Ad Unit'}
                </p>
              </div>

              {/* Sizes column — every supported size as its own chip. The
                  cell wraps so long lists stay readable; chip text is kept
                  small so a row with many sizes doesn't dominate the view. */}
              <div className="min-w-0">
                {adUnit.sizes && adUnit.sizes.length > 0 ? (
                  <div
                    className="flex flex-wrap items-center gap-1"
                    title={adUnit.sizes.join(', ')}
                  >
                    {adUnit.sizes.map((size) => (
                      <span
                        key={size}
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] leading-4 font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300"
                      >
                        {size}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                )}
              </div>

              {/* Last Modified */}
              <div className="min-w-0">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {formatRelativeTime(adUnit.updated_at || adUnit.created_at)}
                </p>
              </div>

              {/* Options */}
              <div className="flex items-center justify-end gap-1 relative">
                {!isProcessing && (
                  <>
                    <button
                      onClick={() => handleOpenNetworkModal(adUnit)}
                      className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                      title="Publish Tags"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    {(() => {
                      const previewLink = getPreviewUrl(adUnit)
                      return previewLink ? (
                        <a
                          href={previewLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                          title="Preview"
                        >
                          <Eye className="h-4 w-4" />
                        </a>
                      ) : (
                        <span className="p-1.5 text-gray-300 dark:text-gray-600 cursor-default" title="No preview available">
                          <Eye className="h-4 w-4" />
                        </span>
                      )
                    })()}
                    <button
                      onClick={() => setContextMenuId(isMenuOpen ? null : adUnit.id)}
                      className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                    >
                      {isCopying ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <MoreVertical className="h-4 w-4" />
                      )}
                    </button>
                  </>
                )}

                {/* Context Menu */}
                <AnimatePresence>
                  {isMenuOpen && (
                    <motion.div
                      ref={contextMenuRef}
                      initial={{ opacity: 0, scale: 0.95, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -4 }}
                      transition={{ duration: 0.12 }}
                      className="absolute right-0 top-full mt-1 z-50 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-1 overflow-hidden"
                    >
                      {(() => {
                        const menuPreviewLink = getPreviewUrl(adUnit)
                        return menuPreviewLink ? (
                          <a
                            href={menuPreviewLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setContextMenuId(null)}
                            className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                          >
                            <Eye className="h-4 w-4 text-gray-400" />
                            Preview
                          </a>
                        ) : null
                      })()}
                      <button
                        onClick={() => handleCopyAdUnit(adUnit)}
                        disabled={isCopying}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors disabled:opacity-50"
                      >
                        <Copy className="h-4 w-4 text-gray-400" />
                        {isCopying ? 'Copying...' : 'Copy Ad Unit'}
                      </button>
                      <button
                        onClick={() => handleOpenNetworkModal(adUnit)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                      >
                        <Download className="h-4 w-4 text-gray-400" />
                        Publish Tags
                      </button>
                      <button
                        onClick={() => {
                          onViewDashboard?.(adUnit.ad_unit_id || adUnit.id)
                          setContextMenuId(null)
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                      >
                        <BarChart3 className="h-4 w-4 text-gray-400" />
                        View Dashboard
                      </button>
                      <div className="my-1 border-t border-gray-100 dark:border-gray-700/60" />
                      <button
                        onClick={() => {
                          setDeletingUnit(adUnit)
                          setContextMenuId(null)
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )
        })}
        </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between py-3 pl-4 pr-4 lg:pr-8">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {(pagination.current_page - 1) * 10 + 1}
              </span>{' '}
              to{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {Math.min(pagination.current_page * 10, pagination.total_count)}
              </span>{' '}
              of{' '}
              <span className="font-medium text-gray-900 dark:text-white">{pagination.total_count}</span> results
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => handlePageChange(1)} disabled={pagination.current_page <= 1 || isFetching} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="First page">
                <ChevronsLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
              <button onClick={() => handlePageChange(pagination.current_page - 1)} disabled={pagination.current_page <= 1 || isFetching} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Previous page">
                <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
              <div className="flex items-center gap-1 mx-1">
                {getPageNumbers().map((page, index) =>
                  page === 'ellipsis' ? (
                    <span key={`ellipsis-${index}`} className="px-2 text-gray-400 dark:text-gray-500">...</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      disabled={isFetching}
                      className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                        pagination.current_page === page
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>
              <button onClick={() => handlePageChange(pagination.current_page + 1)} disabled={pagination.current_page >= pagination.total_pages || isFetching} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Next page">
                <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
              <button onClick={() => handlePageChange(pagination.total_pages)} disabled={pagination.current_page >= pagination.total_pages || isFetching} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Last page">
                <ChevronsRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deletingUnit && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full border border-gray-200 dark:border-gray-700 p-6"
            >
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                Delete Ad Unit
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
                Are you sure you want to delete "{deletingUnit.name || deletingUnit.content_type || `Ad Unit #${deletingUnit.id}`}"? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingUnit(null)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAdUnit}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Network Selection Modal */}
      <AnimatePresence>
        {networkModalAdUnit && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden border border-gray-200 dark:border-gray-700"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select Network</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Ad Unit: {networkModalAdUnit.name || networkModalAdUnit.content_type || `#${networkModalAdUnit.ad_unit_id || networkModalAdUnit.id}`}
                  </p>
                </div>
                <button
                  onClick={() => setNetworkModalAdUnit(null)}
                  disabled={publishingNetwork !== null}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-4 border-b border-gray-100 dark:border-gray-700/50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search networks..."
                    value={networkSearchQuery}
                    onChange={(e) => setNetworkSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="overflow-y-auto max-h-[calc(85vh-180px)] p-2">
                {filteredNetworks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No networks found matching "{networkSearchQuery}"
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {filteredNetworks.map((network) => {
                      const isThisPublishing = publishingNetwork === network.key
                      const isAnyPublishing = publishingNetwork !== null
                      return (
                        <button
                          key={network.key}
                          onClick={() => handlePublishTags(network.key)}
                          disabled={isAnyPublishing}
                          className={`p-3 text-left border rounded-lg transition-all relative ${
                            isThisPublishing
                              ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700'
                              : isAnyPublishing
                              ? 'bg-gray-100 dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                              : 'bg-gray-50 dark:bg-gray-900 hover:bg-primary-50 dark:hover:bg-primary-900/20 border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 group'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className={`text-sm font-medium ${
                                isThisPublishing
                                  ? 'text-primary-700 dark:text-primary-400'
                                  : 'text-gray-900 dark:text-white group-hover:text-primary-700 dark:group-hover:text-primary-400'
                              }`}>
                                {network.name}
                              </span>
                              <span className="block text-xs text-gray-400 mt-0.5">{network.key}</span>
                            </div>
                            {isThisPublishing && (
                              <Loader2 className="h-5 w-5 text-primary-600 dark:text-primary-400 animate-spin" />
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Tags Display Modal */}
      <AnimatePresence>
        {tagsModalData && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-2">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-5xl w-full h-[96vh] overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Ad Tags - {NETWORK_OPTIONS.find(n => n.key === tagsModalData.response.network)?.name || tagsModalData.response.network}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {tagsModalData.response.js_tags.length} tag{tagsModalData.response.js_tags.length !== 1 ? 's' : ''} generated for{' '}
                    {tagsModalData.adUnit.name || tagsModalData.adUnit.content_type}
                  </p>
                </div>
                <button
                  onClick={() => setTagsModalData(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {tagsModalData.response.js_tags.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                      <FileText className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">No tags available for this network.</p>
                  </div>
                ) : (
                  tagsModalData.response.js_tags.map((tag, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-3 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3">
                          <span className="px-2.5 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 text-sm font-medium rounded">
                            {tag.size}
                          </span>
                          {tag.ad_unit_file_size && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {(tag.ad_unit_file_size / 1024).toFixed(1)} KB
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleCopyTag(tag, index)}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            copiedTagIndex === index
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                          }`}
                        >
                          {copiedTagIndex === index ? (
                            <>
                              <Check className="h-4 w-4" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              Copy Tag
                            </>
                          )}
                        </button>
                      </div>
                      <div className="p-4">
                        <pre className="text-xs text-gray-300 bg-gray-900 dark:bg-black p-4 rounded-lg overflow-auto max-h-64 whitespace-pre-wrap break-all font-mono leading-relaxed">
                          <code>{tag.content}</code>
                        </pre>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-between items-center flex-shrink-0">
                <button
                  onClick={() => {
                    setTagsModalData(null)
                    setNetworkModalAdUnit(tagsModalData.adUnit)
                  }}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  &larr; Choose Different Network
                </button>
                <button
                  onClick={() => setTagsModalData(null)}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Create Ad Unit Modal */}
      {campaignId && (
        <CreateAdUnitModal
          isOpen={isCreateModalOpen}
          campaignId={campaignId}
          advertiserId={advertiserId}
          onClose={() => setIsCreateModalOpen(false)}
          onCreated={(id) => {
            setIsCreateModalOpen(false)
            onOpenAdUnit?.(id)
          }}
        />
      )}
    </div>
  )
}
